import { useApi } from '@/config/apiFetcher'
import { useUserStore } from '@/store/userStore'
import type {
  CreateDeviceBody,
  CreateDeviceResponse,
  OnboardingDevice,
  UpdateDeviceBody,
} from '@/types/onboarding.types'
import log from '@/utils/useLogger'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { computed, type Ref } from 'vue'
import { useProjectsStore } from '@/store/projectStore'

export const ORG_DEVICES_QUERY_KEY = (orgId: string) => ['devices', orgId] as const

export default () => {
  const api = useApi()
  const queryClient = useQueryClient()
  const baseUrl = import.meta.env.VITE_APP_HTTP_ENPOINT
  const { isLoggedIn } = storeToRefs(useUserStore())

  const useOrgDevicesQuery = (orgId: Ref<string | null | undefined>) =>
    useQuery({
      queryKey: computed(() => ORG_DEVICES_QUERY_KEY(orgId.value || '')),
      queryFn: async () => {
        const res = await api.get(
          `${baseUrl}device-management/devices?organisationID=${encodeURIComponent(orgId.value || '')}`,
        )
        if (res.status !== 200) {
          throw new Error('Failed to fetch onboarding devices')
        }
        return (res.data?.deviceIDList ?? []) as OnboardingDevice[]
      },
      enabled: computed(() => !!isLoggedIn.value && !!baseUrl && !!orgId.value),
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 2,
      retryDelay: 1000,
      refetchOnWindowFocus: false,
    })

  const useUpdateDeviceMutation = () =>
    useMutation({
      mutationFn: async (body: UpdateDeviceBody) => {
        const res = await api.post(`${baseUrl}device-management/update-device`, body)
        if (res.status !== 200) throw new Error('Failed to update device')
        return (res.data?.device ?? res.data) as OnboardingDevice
      },
      onSuccess: (_data, vars) => {
        queryClient.invalidateQueries({
          queryKey: ORG_DEVICES_QUERY_KEY(vars.organisationID),
        })
      },
      onError: (error) => log.error('Error updating onboarding device:', error),
    })

  const useCreateDeviceMutation = () =>
    useMutation({
      mutationFn: async (body: CreateDeviceBody) => {
        // Stamp the active project (FLY3D-938) — a device created while a
        // project is selected belongs to it; backend falls back to
        // {org}_default otherwise.
        const { activeProjectId } = storeToRefs(useProjectsStore())
        if (activeProjectId.value && !body.projectID) {
          body = { ...body, projectID: activeProjectId.value }
        }
        const res = await api.post(`${baseUrl}device-management/create-device`, body)
        if (res.status !== 200) throw new Error('Failed to create device')
        const data = res.data as CreateDeviceResponse

        if (data.deviceID) {
          const cognitoEndpoint = import.meta.env.VITE_COGNITO_DEVICE_ENDPOINT
          try {
            await api.post(cognitoEndpoint, {
              device_id: data.deviceID,
              organization_id: body.organisationID,
            })
          } catch (err) {
            log.error('Add device to Cognito error', err)
            throw new Error(
              'Device created but Cognito registration failed. Please contact support.',
            )
          }
        }

        return data
      },
      onSuccess: (_data, vars) => {
        queryClient.invalidateQueries({
          queryKey: ORG_DEVICES_QUERY_KEY(vars.organisationID),
        })
      },
      onError: (error) => log.error('Error creating onboarding device:', error),
    })

  return {
    useOrgDevicesQuery,
    useUpdateDeviceMutation,
    useCreateDeviceMutation,
  }
}
